// ...existing code...
import React, { useEffect, useMemo, useState } from "react";

const LS_KEY = "appAccessLogs";

function readLogs() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; }
}
function writeLogs(logs) { localStorage.setItem(LS_KEY, JSON.stringify(logs)); }

function addLog(evt, meta = {}) {
  const logs = readLogs();
  logs.unshift({
    ts: new Date().toISOString(),
    evt,
    ...meta,
  });
  writeLogs(logs.slice(0, 2000)); // cap
}

export default function AccessLogs() {
  const [enabled, setEnabled] = useState(false);
  const [logs, setLogs] = useState(() => readLogs());

  // click + navigation capture
  useEffect(() => {
    if (!enabled) return;
    const onClick = (e) => {
      // prefer an Element target (text nodes may be target); fall back safely
      const el = e.target instanceof Element ? e.target : (e.target && e.target.parentElement) || null;
      const text = (el?.innerText || "").trim().slice(0, 80);
      const tag = el?.tagName;
      addLog("click", { tag, text });
      setLogs(readLogs());
    };
    const onNavigate = () => {
      addLog("navigate", { url: window.location.pathname + window.location.search });
      setLogs(readLogs());
    };
    window.addEventListener("click", onClick, true);
    window.addEventListener("popstate", onNavigate);
    addLog("capture-start", { url: window.location.pathname + window.location.search });
    setLogs(readLogs());
    return () => {
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", onNavigate);
      addLog("capture-stop", {});
      setLogs(readLogs());
    };
  }, [enabled]);

  const csv = useMemo(() => {
    if (!logs.length) return "";
    const headers = Object.keys(logs[0]);
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    for (const r of logs) lines.push(headers.map((h) => esc(r[h])).join(","));
    return lines.join("\n");
  }, [logs]);

  function downloadCSV() {
    if (!csv) return;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "access-logs.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="surface rounded-hard p-5">
      <h1 className="text-ice text-xl font-semibold mb-3">Access Logs</h1>
      <p className="text-mist mb-3">
        Who opened which document and when. (Scaffold; stored locally in your browser for now.)
      </p>

      <div className="mb-4 flex items-center gap-3">
        <button
          className={`chip px-4 py-2 ${enabled ? "outline outline-2 outline-brand" : ""}`}
          onClick={() => setEnabled((v) => !v)}
        >
          {enabled ? "Stop Capture" : "Start Capture"}
        </button>
        <button className="chip px-4 py-2" onClick={() => { writeLogs([]); setLogs([]); }}>
          Clear
        </button>
        <button className="chip px-4 py-2" disabled={!logs.length} onClick={downloadCSV}>
          Export CSV
        </button>
        <div className="text-mist ml-auto">Stored locally (no server yet).</div>
      </div>

      <div className="border border-edge bg-[#0f1c27] max-h-[60vh] overflow-auto p-0">
        <table className="w-full text-sm border-separate border-spacing-0">
          <thead className="sticky top-0 bg-slab text-mist">
            <tr>
              <th className="px-3 py-2 text-left border-b border-edge">Time</th>
              <th className="px-3 py-2 text-left border-b border-edge">Event</th>
              <th className="px-3 py-2 text-left border-b border-edge">Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((r, i) => (
              <tr key={i} className="odd:bg-slab/50">
                <td className="px-3 py-2 border-b border-edge">{new Date(r.ts).toLocaleString()}</td>
                <td className="px-3 py-2 border-b border-edge">{r.evt}</td>
                <td className="px-3 py-2 border-b border-edge">
                  <div className="text-mist/80 break-words">{JSON.stringify(r)}</div>
                </td>
              </tr>
            ))}
            {!logs.length && (
              <tr><td className="px-3 py-8 text-center text-mist" colSpan={3}>No logs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
// ...existing code...